import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import { getFirestore, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import './App.css';

// --- Firebase Configuration ---
/* eslint-disable no-undef */
const firebaseConfig = (typeof __firebase_config !== 'undefined')
    ? JSON.parse(__firebase_config)
    : {
        apiKey: "AIzaSyCvEYrUUcCnyfjC7A6RJjGGZXwk7hxmnUU",
        authDomain: "begin-to-restore-journey.firebaseapp.com",
        projectId: "begin-to-restore-journey",
        storageBucket: "begin-to-restore-journey.appspot.com",
        messagingSenderId: "1013762378420",
        appId: "1:1013762378420:web:e076a59d1294cbcd1ca5e1",
        measurementId: "G-YJ5L0KHYJQ"
    };
const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-platform-hub-app';
/* eslint-enable no-undef */

// --- Content Catalog ---
const contentCatalog = {
    courses: {
        'begin-to-restore': {
            id: 'begin-to-restore',
            title: "Begin to Restore: Intro to Restorative Practices",
            description: "This is more than a course; it's a guided return to the wisdom you already hold within. In a world that pulls us outward, this journey invites you inward: to reconnect with your breath, release inherited burdens, befriend your inner world, and weave healing into your daily life.",
            courseUrl: "https://intro-journey.web.app",
            modules: [
                { title: "Module 1: The Circle Welcome", icon: '🌀' },
                { title: "Module 2: Two Rivers, One Ocean", icon: '🌊' },
                { title: "Module 3: Understanding Through Story", icon: '💚' },
                { title: "Module 4: Like Water - Understanding Conflict", icon: '☀️' },
                { title: "Module 5: The Art of Listening", icon: '🗣️' },
                { title: "Module 6: Speaking Your Truth", icon: '🤝' },
                { title: "Module 7: The Journey of Accountability", icon: '🙏' },
                { title: "Module 8: Living Restoratively", icon: '🌱' },
            ]
        },
    },
    series: {
        'living-restoratively': {
            id: 'living-restoratively',
            title: "Living Restoratively: The Applied Series",
            description: "A collection of practical, focused courses designed to help you apply restorative principles to the most important areas of your life: with yourself, at home, at work, and in your community.",
            modules: [
                { id: 'with-yourself', title: "Living Restoratively: With Yourself", description: "A 4-module supplemental course on reconnecting with your sacred center through breath, pattern-breaking, and self-compassion.", icon: '🧘', status: 'active', courseUrl: 'https://with-yourself-journey.web.app' },
                { id: 'at-home', title: "Living Restoratively: At Home", description: "Apply restorative principles to family meetings, sibling conflicts, parent-child repair, and creating family agreements.", icon: '👨‍👩‍👧‍👦', status: 'active', courseUrl: 'https://at-home-journey.web.app' },
                { id: 'at-work', title: "Living Restoratively: At Work", description: "Address microaggressions, team conflicts, and give restorative feedback to build trust and psychological safety.", icon: '💬', status: 'active', courseUrl: 'https://at-work-journey.web.app/' },
                { id: 'in-community', title: "Living Restoratively: In Community", description: "Engage with neighborhood disputes, community organizing, supporting friends, and practicing active allyship.", icon: '🏡', status: 'coming-soon' },
            ]
        }
    },
    deepDives: [
        { id: 'accountability', title: "Accountability Deep Dive", description: "The 5 Steps, scripts for what to say, and practice scenarios.", url: "https://accountability-deep-dive.web.app", status: 'active' },
        { id: 'conflict', title: "Conflict Resolution Toolkit", description: "De-escalation techniques, power dynamics, and a 6-step resolution process.", url: "https://conflict-resolution-deep-dive.web.app/", status: 'active' },
        { id: 'circles', title: "Facilitating Your First Circle", description: "Preparation, creating sacred space, and managing difficult moments.", url: "https://first-circle-deep-dive.web.app", status: 'active' },
        { id: 'conversations', title: "Restorative Conversations", description: "Scripts and frameworks for preparing parties and following up effectively.", url: "https://restorative-conversations-deep-dive.web.app/", status: 'active' },
        { id: 'resistance', title: "Working with Resistance", description: "Addressing skepticism, building buy-in, and alternative approaches.", status: 'coming-soon' },
        { id: 'harm', title: "Harm Response Protocol", description: "Immediate response to harm, assessing needs, and creating safety.", status: 'coming-soon' },
    ]
};

// --- Shared Components ---
const TopNavBar = ({ currentPage, navigate }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const navRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (navRef.current && !navRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [navRef]);

    const handleNavigate = (page) => {
        navigate(page);
        setIsMenuOpen(false);
    };

    const navItems = [
        { label: 'Home', route: 'home' },
        { label: 'Courses', route: 'courses' },
        { label: 'Kipemantam⁸iganen', route: 'theory' },
        { label: 'Workbooks', route: 'workbooks' },
        { label: 'Contact', route: 'contact' }
    ];

    return (
        <nav ref={navRef} className="river-nav" aria-label="Main navigation">
            <button onClick={() => handleNavigate('home')} className="logo" aria-label="Begin to Restore - Home">
                Begin to Restore
            </button>
            <div className="nav-links">
                {navItems.map((item) => (
                    <button
                        key={item.route}
                        onClick={() => handleNavigate(item.route)}
                        className={currentPage === item.route ? 'active' : ''}
                    >
                        {item.label}
                    </button>
                ))}
            </div>
            <button className="mobile-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-expanded={isMenuOpen} aria-label="Toggle navigation menu">
                <svg width="24" height="24" stroke="currentColor" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    {isMenuOpen
                        ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />}
                </svg>
            </button>
            <div className={`mobile-menu ${isMenuOpen ? 'show' : ''}`}>
                {navItems.map((item) => (
                    <button
                        key={item.route}
                        onClick={() => handleNavigate(item.route)}
                        className={currentPage === item.route ? 'active' : ''}
                    >
                        {item.label}
                    </button>
                ))}
            </div>
        </nav>
    );
};

const Footer = () => (
    <footer className="river-footer">
        <div className="mark">Begin to Restore</div>
        <p className="closing">The river holds.</p>
        <p className="meta">&copy; {new Date().getFullYear()} &middot; Developed by an Indigenous woman, committed to making restorative knowledge accessible to all.</p>
    </footer>
);

// Fades content up into view the first time it scrolls into the viewport
const Reveal = ({ children, delay = 0, className = '' }) => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;
        if (!('IntersectionObserver' in window)) {
            setVisible(true);
            return;
        }
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setVisible(true);
                observer.disconnect();
            }
        }, { threshold: 0.12 });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            className={`reveal ${visible ? 'is-visible' : ''} ${className}`.trim()}
            style={delay ? { transitionDelay: `${delay}ms` } : undefined}
        >
            {children}
        </div>
    );
};

// --- Page Components ---
const HomePage = ({ navigate }) => (
    <div>
        <div className="river-hero">
            <div className="water-particles" aria-hidden="true">
                <span></span><span></span><span></span><span></span>
                <span></span><span></span><span></span><span></span>
            </div>
            <span className="hero-label">A Living Practice</span>
            <h1>Learning to Heal, <span className="hero-accent">Together</span></h1>
            <div className="hero-rule" aria-hidden="true"></div>
            <p className="hero-subtitle">
                Begin to Restore is a learning space dedicated to sharing restorative practices rooted in Indigenous wisdom. We believe this knowledge is a living medicine, meant to be shared and adapted for the healing of all our communities.
            </p>
            <div className="hero-actions">
                <button onClick={() => navigate('courses')} className="river-btn primary">Begin the Journey &rarr;</button>
                <button onClick={() => navigate('theory')} className="river-btn copper">Explore the Framework</button>
            </div>
            <div className="hero-waves" aria-hidden="true">
                <svg className="wave wave-back" viewBox="0 0 2880 120" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0,70 C120,46 240,46 360,70 S600,94 720,70 S960,46 1080,70 S1320,94 1440,70 S1680,46 1800,70 S2040,94 2160,70 S2400,46 2520,70 S2760,94 2880,70 L2880,120 L0,120 Z" fill="rgba(184, 152, 56, 0.07)" />
                </svg>
                <svg className="wave wave-front" viewBox="0 0 2880 120" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0,78 C120,56 240,56 360,78 S600,100 720,78 S960,56 1080,78 S1320,100 1440,78 S1680,56 1800,78 S2040,100 2160,78 S2400,56 2520,78 S2760,100 2880,78 L2880,120 L0,120 Z" fill="rgba(58, 143, 159, 0.12)" />
                </svg>
            </div>
        </div>

        <div className="feature-grid">
            <Reveal className="feature-item">
                <div className="feature-icon" aria-hidden="true">🌱</div>
                <h3>Lifelong Learning</h3>
                <p>Restorative practice is a lifelong journey, not a destination. Our resources are designed to meet you where you are and support your continued growth.</p>
            </Reveal>
            <Reveal className="feature-item" delay={120}>
                <div className="feature-icon" aria-hidden="true">💧</div>
                <h3>Accessible Knowledge</h3>
                <p>Everyone deserves access to tools for healing and connection. Our core trainings and resources are offered freely as a commitment to community care.</p>
            </Reveal>
            <Reveal className="feature-item" delay={240}>
                <div className="feature-icon" aria-hidden="true">🛶</div>
                <h3>Indigenous Roots</h3>
                <p>This platform was developed by an Indigenous woman, weaving together ancestral wisdom with modern application to honor the deep roots of restorative justice.</p>
            </Reveal>
        </div>

        <div className="section-divider"></div>

        <Reveal className="journey-steps">
            <h2>Your Learning Journey</h2>
            <p className="steps-subtitle">We recommend this path to get the most out of our resources, but feel free to explore in any way that serves you.</p>
            <div className="steps-grid">
                <div className="step-item">
                    <div className="step-number">1</div>
                    <h3>Build Your Foundation</h3>
                    <p>Start with our core 8-module course, <button onClick={() => navigate('courses')} style={{ background: 'none', border: 'none', color: 'var(--dawn-copper)', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}>Begin to Restore</button>, to learn the foundational principles.</p>
                </div>
                <div className="step-item">
                    <div className="step-number">2</div>
                    <h3>Apply Your Practice</h3>
                    <p>Deepen your understanding with the <button onClick={() => navigate('courses')} style={{ background: 'none', border: 'none', color: 'var(--dawn-copper)', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}>Living Restoratively Series</button>. We recommend starting "With Yourself."</p>
                </div>
                <div className="step-item">
                    <div className="step-number">3</div>
                    <h3>Sharpen Your Skills</h3>
                    <p>Use our <button onClick={() => navigate('courses')} style={{ background: 'none', border: 'none', color: 'var(--dawn-copper)', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}>Deep-Dive Topics</button> whenever you need to focus on a specific challenge.</p>
                </div>
            </div>
        </Reveal>

        <div className="section-divider"></div>

        <div className="showcase-section">
            <Reveal className="showcase-card">
                <div className="showcase-left">
                    <h3>Kipemantam⁸iganen</h3>
                    <p className="ki-subtitle">"Our Shared Life/Breath"</p>
                    <p>A complete framework for liberation, healing, and community practice rooted in Nipmuc language and worldview, offering concepts that Western systems are only beginning to rediscover.</p>
                </div>
                <div className="showcase-right">
                    <div className="showcase-detail">
                        <span className="detail-icon">🛶</span>
                        <p><strong>The Mishoon Metaphor:</strong> We're all in the same canoe. Harm tips the boat; healing rebalances it.</p>
                    </div>
                    <div className="showcase-detail">
                        <span className="detail-icon">🌅</span>
                        <p><strong>The Arc of Healing:</strong> From being lost, to waking up, to turning back, to thinking beautifully.</p>
                    </div>
                    <div className="showcase-detail">
                        <span className="detail-icon">🤝</span>
                        <p><strong>The Nitap Role:</strong> You are not the expert from the shore. You are the "Fellow Sitter."</p>
                    </div>
                    <div style={{ marginTop: '1.5rem' }}>
                        <button onClick={() => navigate('theory')} className="river-btn copper">
                            Explore the Framework &rarr;
                        </button>
                    </div>
                </div>
            </Reveal>
        </div>

        <Reveal className="lore-quote">
            Like the river, restorative justice does not force its way. It finds the path of least resistance, shaping the landscape not through power but through patience, persistence, and the quiet certainty that water always finds its way home.
            <span className="source">from the Begin to Restore training</span>
        </Reveal>

        <div className="center-action">
            <Reveal className="cta-band">
                <h2>Step Into the Current</h2>
                <p>Every journey begins with a single breath. Find the path that meets you where you are.</p>
                <button onClick={() => navigate('courses')} className="river-btn primary">
                    Explore All Courses &rarr;
                </button>
            </Reveal>
        </div>
    </div>
);

const CoursesPage = ({ onSelectCourse, onSelectSeries }) => (
    <div>
        <div className="page-header">
            <div className="section-label">Learning Paths</div>
            <h1>Our Core Programs</h1>
            <div className="rule"></div>
        </div>

        <div className="river-section">
            <div className="river-card-grid two-col">
                <div className="river-card" onClick={() => onSelectCourse('begin-to-restore')} tabIndex={0} role="button" aria-label={contentCatalog.courses['begin-to-restore'].title} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectCourse('begin-to-restore'); }}}>
                    <div className="card-label">Core Course · 8 Modules</div>
                    <h3>{contentCatalog.courses['begin-to-restore'].title}</h3>
                    <p>{contentCatalog.courses['begin-to-restore'].description}</p>
                    <span className="arrow">View Details &rarr;</span>
                </div>
                <div className="river-card" onClick={() => onSelectSeries('living-restoratively')} tabIndex={0} role="button" aria-label={contentCatalog.series['living-restoratively'].title} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectSeries('living-restoratively'); }}}>
                    <div className="card-label">Applied Series · 4 Courses</div>
                    <h3>{contentCatalog.series['living-restoratively'].title}</h3>
                    <p>{contentCatalog.series['living-restoratively'].description}</p>
                    <span className="arrow">View Details &rarr;</span>
                </div>
            </div>

            <h3 className="sub-heading" style={{ marginTop: '4rem' }}>Deep-Dive Topics</h3>

            <div className="deep-dive-grid">
                {contentCatalog.deepDives.map((dive) => (
                    <div key={dive.id} className="deep-dive-card">
                        <h4>
                            <span className={`status-dot ${dive.status === 'active' ? 'active' : 'coming'}`} aria-hidden="true"></span>
                            {dive.title}
                            <span className="sr-only">{dive.status === 'active' ? '(Available)' : '(Coming soon)'}</span>
                        </h4>
                        <p>{dive.description}</p>
                        {dive.status === 'active' ? (
                            <a href={dive.url} target="_blank" rel="noopener noreferrer" className="river-btn" style={{ textAlign: 'center', display: 'block' }}>
                                View Deep-Dive
                            </a>
                        ) : (
                            <span className="river-btn disabled" style={{ textAlign: 'center', display: 'block' }}>Coming Soon</span>
                        )}
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const CourseHomePage = ({ courseId, onBack }) => {
    const course = contentCatalog.courses[courseId];
    return (
        <div>
            <div className="page-header">
                <div className="section-label">Core Course</div>
                <h1>{course.title}</h1>
                <div className="rule"></div>
                <p className="subtitle">{course.description}</p>
            </div>

            <div className="river-section">
                <button onClick={onBack} className="back-link">&larr; Back to All Programs</button>

                <div style={{ textAlign: 'center', margin: '2rem 0 3rem' }}>
                    <a href={course.courseUrl} target="_blank" rel="noopener noreferrer" className="river-btn copper" style={{ fontSize: '0.8rem', padding: '1.2rem 3rem' }}>
                        Begin Journey &rarr;
                    </a>
                </div>

                <h3 className="sub-heading">What You Will Explore</h3>

                <div className="module-list">
                    {course.modules.map((module, index) => (
                        <div key={index} className="module-item">
                            <span className="module-icon">{module.icon}</span>
                            <h4>{module.title}</h4>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const CourseSeriesPage = ({ seriesId, onBack }) => {
    const series = contentCatalog.series[seriesId];
    return (
        <div>
            <div className="page-header">
                <div className="section-label">Applied Series</div>
                <h1>{series.title}</h1>
                <div className="rule"></div>
                <p className="subtitle">{series.description}</p>
            </div>

            <div className="river-section">
                <button onClick={onBack} className="back-link">&larr; Back to All Programs</button>

                <h3 className="sub-heading">Trainings in this Series</h3>

                {series.modules.map(course => (
                    <div key={course.id} className={`series-item ${course.status === 'coming-soon' ? 'disabled' : ''}`}>
                        <span className="series-icon">{course.icon}</span>
                        <div className="series-info">
                            <h3>{course.title}</h3>
                            <p>{course.description}</p>
                        </div>
                        <div>
                            {course.status === 'active' ? (
                                <a href={course.courseUrl} target="_blank" rel="noopener noreferrer" className="river-btn primary">
                                    Begin Journey
                                </a>
                            ) : (
                                <span className="river-btn disabled">Coming Soon</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const WorkbooksPage = () => {
    const pocketGuideUrl = "https://firebasestorage.googleapis.com/v0/b/begin-to-restore-journey.firebasestorage.app/o/Workbooks%2FBegin%20to%20Restore%20A%20Pocket%20Guide%20to%20Restorative%20Justice.pdf?alt=media&token=81a89497-075d-4345-ba89-355cd3777510";

    return (
        <div>
            <div className="page-header">
                <div className="section-label">Resources</div>
                <h1>Workbooks &amp; Guides</h1>
                <div className="rule"></div>
                <p className="subtitle">Downloadable resources to support your offline practice and deepen your learning.</p>
            </div>

            <div className="river-section">
                <div className="resource-block">
                    <div className="resource-info">
                        <h3>Begin to Restore: A Pocket Guide</h3>
                        <p>This comprehensive introductory workbook is perfect for individuals, groups, or teams. It's filled with reflections, activities, and circle prompts to help you integrate restorative practices into your daily life.</p>
                        <div style={{ marginTop: '1.5rem' }}>
                            <a href={pocketGuideUrl} target="_blank" rel="noopener noreferrer" className="river-btn primary">
                                Download PDF
                            </a>
                        </div>
                    </div>
                </div>

                <div className="theory-card" style={{ textAlign: 'center', opacity: 0.5 }}>
                    <h3>More Workbooks Coming Soon</h3>
                    <p>Worksheets for our Deep Dives and course companions are in development.</p>
                </div>
            </div>
        </div>
    );
};

const TheoryPage = ({ navigate }) => {
    const communityGuideUrl = "https://firebasestorage.googleapis.com/v0/b/begin-to-restore-journey.firebasestorage.app/o/Guides%2FKipemantam8iganen_Community_Guide.pdf?alt=media";

    return (
        <div>
            <div className="page-header" style={{ background: 'radial-gradient(ellipse 80% 50% at 50% 80%, rgba(140,75,55,0.1) 0%, transparent 60%), var(--night)' }}>
                <div className="section-label">The Indigenous Heart</div>
                <h1>Kipemantam⁸iganen</h1>
                <div className="rule"></div>
                <p className="subtitle">"Our Shared Life/Breath": A framework for liberation, healing, and community practice rooted in Nipmuc language and worldview.</p>
            </div>

            <div className="river-section">
                <div className="theory-card">
                    <h3 style={{ textAlign: 'center', fontSize: '1.4rem' }}>The Theoretical Foundation</h3>
                    <p>This is more than restorative justice with an "Indigenous flavor." This is a complete framework rooted in a worldview that never separated healing from freedom, that never separated personal repair from collective return.</p>
                    <p>The Nipmuc language (spoken for thousands of years) offers concepts that Western psychology and justice systems are only beginning to rediscover. But we didn't lose these concepts because they stopped working. We lost them because they were taken.</p>
                    <p style={{ marginBottom: 0 }}>What you will explore is an act of reclamation. When you use this framework, you're not just learning a new technique; you're participating in the survival of a worldview.</p>
                </div>

                <div className="river-card-grid two-col" style={{ margin: '2rem 0' }}>
                    <div className="theory-card">
                        <h3>🛶 The Mishoon Metaphor</h3>
                        <p>Our guiding image is the dugout canoe, carved from a single log, requiring everyone aboard to balance together. When someone causes harm, we don't throw them overboard. We help them shift weight back to center.</p>
                    </div>
                    <div className="theory-card">
                        <h3>💚 The Four Liberations</h3>
                        <p>Pohqua (clearing), Quinu (turning back), Taking Off the Coat (decolonization), and Touohkomuk (the open place). Liberation is not escape; it's clearing the debris so life can flow.</p>
                    </div>
                    <div className="theory-card">
                        <h3>🌅 The Arc of the Work</h3>
                        <p>From ⁸anisau (being lost) → T⁸kin (waking up) → Quinu (the pivot) → Miska⁸elendam (thinking beautifully). Healing follows a natural movement, like the sun rising.</p>
                    </div>
                    <div className="theory-card">
                        <h3>🤝 The Nitap Role</h3>
                        <p>You are not the expert from the shore. You are the "Fellow Sitter," one who stays, one who accompanies. You are in the Mishoon with everyone else.</p>
                    </div>
                </div>

                <div className="resource-block">
                    <div className="resource-info">
                        <h3>📖 The Full Community Guide</h3>
                        <p>Download the complete Kipemantam⁸iganen Community Guide (PDF). This comprehensive document includes the full theoretical framework, all Nipmuc terms with pronunciations, practitioner guidance, and circle facilitation resources.</p>
                        <div style={{ marginTop: '1.5rem' }}>
                            <a href={communityGuideUrl} target="_blank" rel="noopener noreferrer" className="river-btn primary">Download PDF</a>
                        </div>
                    </div>
                </div>

                <div className="section-divider"></div>

                <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
                    <h3 className="sub-heading">Begin the Shared Breath Journey</h3>
                    <p style={{ color: 'var(--text-muted)', maxWidth: 600, margin: '0 auto 2rem' }}>
                        Explore all 10 modules of the Kipemantam⁸iganen framework, complete with teachings, reflections, and practitioner tools.
                    </p>
                    <a href="https://shared-breath-journey.web.app" target="_blank" rel="noopener noreferrer" className="river-btn copper" style={{ fontSize: '0.8rem', padding: '1.2rem 3rem' }}>
                        🛶 Begin the Journey
                    </a>
                </div>

                <div className="section-divider"></div>

                <h3 className="sub-heading">🌿 Extensions of Learning</h3>
                <p style={{ textAlign: 'center', color: 'var(--text-muted)', maxWidth: 600, margin: '0 auto 2rem' }}>
                    The Kipemantam⁸iganen framework extends into every corner of life. These specialized paths take the core teachings and apply them to specific contexts.
                </p>

                <div className="path-card">
                    <span className="path-icon">👶</span>
                    <div style={{ flex: 1 }}>
                        <h3>The Kikinam⁸ Parenting Path</h3>
                        <p className="path-tagline">Moving from Control to Guidance</p>
                        <p>Learn to parent from a place of <em>Kikinam⁸</em>: showing the marks on the trail rather than forcing compliance. Includes practical tools, interactive scenarios, the Auntie Map for building your village, and daily practice prompts.</p>
                        <a href="https://kikinam8-parenting.web.app" target="_blank" rel="noopener noreferrer" className="river-btn copper">
                            Begin This Path &rarr;
                        </a>
                    </div>
                </div>

                <div className="path-card">
                    <span className="path-icon">🏢</span>
                    <div style={{ flex: 1 }}>
                        <h3>The Nitap Leadership Path</h3>
                        <p className="path-tagline">Leading from Alongside</p>
                        <p>Apply the Mishoon metaphor to organizational leadership. Learn to lead as a Nitap, one who stays, one who accompanies, rather than commanding from the shore. Includes scenario practice, the Leader's Coat exercise, and your own Support Map.</p>
                        <a href="https://nitap-leadership.web.app" target="_blank" rel="noopener noreferrer" className="river-btn copper">
                            Begin This Path &rarr;
                        </a>
                    </div>
                </div>

                <div className="path-card">
                    <span className="path-icon">💔</span>
                    <div style={{ flex: 1 }}>
                        <h3>The Noohshauonk Grief Path</h3>
                        <p className="path-tagline">Turning Back to What Was Lost</p>
                        <p>A specialized path for processing grief through the framework. Using the arc of ⁸anisau to Miska⁸elendam to find your way through loss, whether you carry it spiritually, psychologically, or simply as a relationship that changed form but never ended.</p>
                        <a href="https://grief-path-deep-dive.web.app" target="_blank" rel="noopener noreferrer" className="river-btn copper">
                            Begin This Path &rarr;
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ContactPage = ({ db, userId }) => {
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [status, setStatus] = useState('idle');
    const [formError, setFormError] = useState('');

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
        if (formError) setFormError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.email || !formData.message) {
            setFormError('Please fill out all fields.');
            return;
        }
        setFormError('');
        setStatus('submitting');
        try {
            const submissionsCollection = collection(db, "artifacts", appId, "public", "data", "contactSubmissions");
            await addDoc(submissionsCollection, {
                ...formData,
                userId: userId || 'anonymous',
                submittedAt: serverTimestamp()
            });
            setStatus('success');
            setFormData({ name: '', email: '', message: '' });
        } catch (err) {
            console.error("Error submitting contact form:", err);
            setStatus('error');
        }
    };

    return (
        <div>
            <div className="page-header">
                <div className="section-label">Connect</div>
                <h1>Get In Touch</h1>
                <div className="rule"></div>
                <p className="subtitle">Have questions, feedback, or want to collaborate? We'd love to hear from you.</p>
            </div>

            <div className="river-section" style={{ maxWidth: 600 }}>
                {status === 'success' ? (
                    <div className="theory-card" style={{ textAlign: 'center' }}>
                        <h3>Thank You!</h3>
                        <p>Your message has been sent successfully. We'll get back to you as soon as we can.</p>
                        <button onClick={() => setStatus('idle')} className="river-btn primary" style={{ marginTop: '1rem' }}>Send Another Message</button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="river-form" noValidate>
                        {formError && <p role="alert" style={{ color: '#f0a0a0', marginBottom: '1rem', fontFamily: "'JetBrains Mono', monospace", fontSize: '0.8rem' }}>{formError}</p>}

                        <label htmlFor="name">Name</label>
                        <input type="text" id="name" value={formData.name} onChange={handleChange} aria-required="true" aria-invalid={formError && !formData.name ? 'true' : undefined} />

                        <label htmlFor="email">Email</label>
                        <input type="email" id="email" value={formData.email} onChange={handleChange} aria-required="true" aria-invalid={formError && !formData.email ? 'true' : undefined} />

                        <label htmlFor="message">Message</label>
                        <textarea id="message" rows="5" value={formData.message} onChange={handleChange} aria-required="true" aria-invalid={formError && !formData.message ? 'true' : undefined}></textarea>

                        <div style={{ textAlign: 'center' }}>
                            <button type="submit" disabled={status === 'submitting'} className="river-btn primary" style={{ opacity: status === 'submitting' ? 0.5 : 1 }}>
                                {status === 'submitting' ? 'Submitting...' : 'Submit Message'}
                            </button>
                            {status === 'error' && <p role="alert" style={{ color: '#f0a0a0', marginTop: '1rem' }}>Something went wrong. Please try again.</p>}
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};


// --- Main App Component ---
export default function App() {
    const [db, setDb] = useState(null);
    const [userId, setUserId] = useState(null);
    const [status, setStatus] = useState('initializing');
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState('home');
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [selectedSeriesId, setSelectedSeriesId] = useState(null);

    useEffect(() => {
        try {
            const app = initializeApp(firebaseConfig);
            const authInstance = getAuth(app);
            const dbInstance = getFirestore(app);

            const unsubscribe = onAuthStateChanged(authInstance, async (user) => {
                let currentUser = user;
                if (!currentUser) {
                    try {
                        /* eslint-disable no-undef */
                        const token = (typeof __initial_auth_token !== 'undefined') ? __initial_auth_token : null;
                        /* eslint-enable no-undef */
                        if (token) {
                            const userCredential = await signInWithCustomToken(authInstance, token);
                            currentUser = userCredential.user;
                        } else {
                            const userCredential = await signInAnonymously(authInstance);
                            currentUser = userCredential.user;
                        }
                    } catch (authError) {
                        console.warn("Firebase sign-in unavailable, running in preview mode:", authError.message);
                        // Still show the app without database features
                        setStatus('ready');
                        return;
                    }
                }
                setUserId(currentUser.uid);
                setDb(dbInstance);
                setStatus('ready');
            });
            return () => unsubscribe();
        } catch (initError) {
            console.warn("Firebase initialization failed, running in preview mode:", initError.message);
            // Still show the app without database features
            setStatus('ready');
        }
    }, []);

    const navigate = (page) => {
        setCurrentPage(page);
        setSelectedCourseId(null);
        setSelectedSeriesId(null);
        window.scrollTo(0, 0);
    };

    const handleSelectCourse = (courseId) => {
        setSelectedCourseId(courseId);
        setCurrentPage('courseHome');
        window.scrollTo(0, 0);
    };

    const handleSelectSeries = (seriesId) => {
        setSelectedSeriesId(seriesId);
        setCurrentPage('seriesHome');
        window.scrollTo(0, 0);
    };

    const handleBackToCourses = () => {
        setCurrentPage('courses');
        setSelectedCourseId(null);
        setSelectedSeriesId(null);
        window.scrollTo(0, 0);
    };

    const renderContent = () => {
        if (status === 'initializing') {
            return <div className="river-loading">Initializing...</div>;
        }

        if (selectedCourseId) {
            return <CourseHomePage courseId={selectedCourseId} onBack={handleBackToCourses} />;
        }
        if (selectedSeriesId) {
            return <CourseSeriesPage seriesId={selectedSeriesId} onSelectCourse={handleSelectCourse} onBack={handleBackToCourses} />;
        }

        switch (currentPage) {
            case 'home':
                return <HomePage navigate={navigate} />;
            case 'courses':
                return <CoursesPage onSelectCourse={handleSelectCourse} onSelectSeries={handleSelectSeries} />;
            case 'workbooks':
                return <WorkbooksPage />;
            case 'theory':
                return <TheoryPage navigate={navigate} />;
            case 'contact':
                return <ContactPage db={db} userId={userId} />;
            default:
                return <HomePage navigate={navigate} />;
        }
    };

    return (
        <div style={{ background: 'var(--night)', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <a href="#main-content" className="skip-link">Skip to content</a>
            <TopNavBar currentPage={currentPage} navigate={navigate} />
            <main id="main-content" style={{ flexGrow: 1, paddingTop: '3.5rem' }}>
                {renderContent()}
            </main>
            <Footer />
        </div>
    );
}
